import { useState, useEffect } from 'react';
import { Calculator, ArrowRight, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';

export const RoiCalculator = () => {
  const [leadsPerMonth, setLeadsPerMonth] = useState(50);
  const [avgJobValue, setAvgJobValue] = useState(15000);
  const [closeRate, setCloseRate] = useState(25);
  const [animatedRevenue, setAnimatedRevenue] = useState(0);

  // Calculate potential lift (30% improvement as per case study)
  const currentMonthlyJobs = (leadsPerMonth * closeRate) / 100;
  const additionalJobs = currentMonthlyJobs * 0.30;
  const additionalMonthlyRevenue = additionalJobs * avgJobValue;
  const additionalAnnualRevenue = additionalMonthlyRevenue * 12;

  // Animate the revenue number
  useEffect(() => {
    const duration = 500;
    const steps = 30;
    const increment = additionalAnnualRevenue / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        setAnimatedRevenue(additionalAnnualRevenue);
        clearInterval(timer);
      } else {
        setAnimatedRevenue(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [additionalAnnualRevenue]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const scrollToCta = () => {
    const element = document.getElementById('cta-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="roi-calculator" className="py-20 md:py-28 bg-slate-900" data-testid="roi-calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-orange-500 font-semibold text-sm uppercase tracking-wider mb-3">
            The Math Doesn't Lie
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Calculate Your Potential Revenue Lift
          </h2>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Based on our client data: faster response times typically increase booked inspections by 30%. See what that means for your business.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Calculator Inputs */}
          <Card className="bg-slate-800 border-slate-700 rounded-sm">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-white font-heading text-xl">
                <Calculator className="w-6 h-6 text-orange-500" />
                Your Numbers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Leads Per Month */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label htmlFor="leads-slider" className="text-slate-300 font-medium">Leads Per Month</label>
                  <span className="font-heading text-2xl font-bold text-white" data-testid="leads-value">
                    {leadsPerMonth}
                  </span>
                </div>
                <Slider
                  id="leads-slider"
                  aria-label="Leads per month"
                  value={[leadsPerMonth]}
                  onValueChange={(value) => setLeadsPerMonth(value[0])}
                  min={10}
                  max={200}
                  step={5}
                  className="py-2"
                  data-testid="leads-slider"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>10</span>
                  <span>200</span>
                </div>
              </div>

              {/* Average Job Value */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label htmlFor="job-value-slider" className="text-slate-300 font-medium">Average Job Value</label>
                  <span className="font-heading text-2xl font-bold text-white" data-testid="job-value">
                    {formatCurrency(avgJobValue)}
                  </span>
                </div>
                <Slider
                  id="job-value-slider"
                  aria-label="Average job value"
                  value={[avgJobValue]}
                  onValueChange={(value) => setAvgJobValue(value[0])}
                  min={5000}
                  max={50000}
                  step={1000}
                  className="py-2"
                  data-testid="job-value-slider"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>$5,000</span>
                  <span>$50,000</span>
                </div>
              </div>

              {/* Close Rate */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label htmlFor="close-rate-slider" className="text-slate-300 font-medium">Current Close Rate</label>
                  <span className="font-heading text-2xl font-bold text-white" data-testid="close-rate-value">
                    {closeRate}%
                  </span>
                </div>
                <Slider
                  id="close-rate-slider"
                  aria-label="Current close rate"
                  value={[closeRate]}
                  onValueChange={(value) => setCloseRate(value[0])}
                  min={10}
                  max={50}
                  step={1}
                  className="py-2"
                  data-testid="close-rate-slider"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>10%</span>
                  <span>50%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-6">
            <Card className="bg-green-900/30 border-green-700 rounded-sm">
              <CardContent className="p-8">
                <p className="text-green-400 font-semibold text-sm uppercase tracking-wider mb-2">
                  Potential Annual Revenue Lift
                </p>
                <p className="font-heading text-5xl sm:text-6xl font-bold text-green-400 animate-count-up" data-testid="annual-revenue-lift">
                  {formatCurrency(animatedRevenue)}
                </p>
                <p className="text-green-300 mt-3 text-sm">
                  Based on 30% improvement in booked inspections
                </p>
              </CardContent>
            </Card>

            {/* Breakdown */}
            <Card className="bg-slate-800 border-slate-700 rounded-sm">
              <CardContent className="p-6 space-y-4">
                <h4 className="text-white font-heading text-lg font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  How We Calculate This
                </h4>
                <div className="space-y-3 text-slate-300">
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span>Current monthly jobs</span>
                    <span className="font-semibold text-white">{currentMonthlyJobs.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span>Additional jobs (30% lift)</span>
                    <span className="font-semibold text-green-400">+{additionalJobs.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span>Additional monthly revenue</span>
                    <span className="font-semibold text-green-400">{formatCurrency(additionalMonthlyRevenue)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span>System cost (monthly)</span>
                    <span className="font-semibold text-orange-400">-$1,500</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button 
              onClick={scrollToCta}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-base rounded-sm active:scale-[0.98] transition-transform"
              data-testid="calculator-cta"
            >
              See If You Qualify
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoiCalculator;
